import { useState, type ReactNode } from 'react';
import { Cloud, Globe, Lock, RefreshCw } from 'lucide-react';
import { Banner, BrandIcon, Card, EmptyState, IconButton, Select, TableSkeleton } from '@/components/ui';
import { ErrorState } from '@/components/ErrorState';
import { CacheSection } from '@/components/sites/dns/CacheSection';
import { NameServersSection } from '@/components/sites/dns/NameServersSection';
import { RecordsSection } from '@/components/sites/dns/RecordsSection';
import { RedirectsSection } from '@/components/sites/dns/RedirectsSection';
import { SslSection } from '@/components/sites/dns/SslSection';
import { useRefreshDns, useSiteDns } from '@/hooks/useDomains';
import type { SiteZoneView } from '@/types/api';

/** The parts of the Domains tab that belong to a registered domain rather than to one name. */
export type ZoneSection = 'records' | 'ssl' | 'redirects' | 'cache' | 'ns';

/** What every section is given: the domain, and whether this operator may change it. */
export interface ZoneSectionProps {
  readonly siteId: string;
  readonly zone: SiteZoneView;
  readonly may: boolean;
}

const ZONE_STATUS: Record<string, string> = { active: 'зона активна', pending: 'зона ждёт смены NS', moved: 'NS переехали', deleted: 'зона удалена' };

/**
 * The DNS and Cloudflare side of a site's names (ADR-0032), one section at a
 * time. Read from 2d2s once for all the sections; a site's names nearly always
 * live under one registered domain, and a picker appears when they do not.
 * Changing anything needs the domain to carry a write tag in 2d2s.
 */
export function ZoneSections({
  siteId,
  section,
  mayManage,
}: {
  readonly siteId: string;
  readonly section: ZoneSection;
  readonly mayManage: boolean;
}): ReactNode {
  const dns = useSiteDns(siteId, true);
  const refresh = useRefreshDns(siteId);
  const [picked, setPicked] = useState<string | null>(null);

  if (dns.isPending) {
    return (
      <Card flush>
        <TableSkeleton rows={4} columns={4} />
      </Card>
    );
  }
  if (dns.isError) return <ErrorState error={dns.error} fallback="Не удалось прочитать DNS из 2d2s" />;
  if (!dns.data.available) {
    return <Banner tone="warning" icon={Cloud}>Интеграция с 2d2s не настроена.</Banner>;
  }

  const zones = dns.data.zones;
  const zone = zones.find((candidate) => candidate.apex === picked) ?? zones[0];
  if (!zone) {
    return (
      <Card flush>
        <EmptyState icon={Globe} message="У сайта нет доменов" />
      </Card>
    );
  }
  const may = mayManage && zone.writable && zone.domainId !== null;
  const writeTags = dns.data.writeTags;

  return (
    <div className="stack">
      <div className="row row-wrap" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="row-tight row-wrap text-sm muted">
          {zones.length > 1 ? (
            <Select label="Домен" size="sm" value={zone.apex} onChange={setPicked} options={zones.map((item) => ({ value: item.apex, label: item.apex }))} />
          ) : (
            <span className="strong mono">{zone.apex}</span>
          )}
          {zone.cloudflare ? (
            <span className="platform-name">
              <BrandIcon name="cloudflare" size={14} />
              Cloudflare{zone.zoneStatus ? `, ${ZONE_STATUS[zone.zoneStatus] ?? zone.zoneStatus}` : ''}
            </span>
          ) : zone.domainId ? (
            <span>· DNS регистратора</span>
          ) : null}
          <span>· имена сайта: {zone.names.join(', ')}</span>
        </div>
        <IconButton icon={RefreshCw} size="sm" variant="ghost" label="Перечитать из 2d2s" disabled={dns.isFetching} onClick={() => void refresh()} />
      </div>

      {zone.problem ? <Banner tone="warning" icon={Cloud}>{zone.problem}</Banner> : null}
      {section !== 'ns' && zone.domainId && !zone.writable ? (
        <Banner tone="info" icon={Lock}>
          {writeTags.length > 0
            ? `Только просмотр: изменение доступно для доменов с тегом ${writeTags.map((tag) => `«${tag}»`).join(', ')} в 2d2s.`
            : 'Только просмотр: не заданы теги доменов для изменения (TWOD2S_DOMAIN_TAGS).'}
        </Banner>
      ) : null}

      {zone.domainId ? (
        section === 'records' ? (
          <RecordsSection siteId={siteId} zone={zone} may={may} />
        ) : section === 'ssl' ? (
          <SslSection siteId={siteId} zone={zone} may={may} />
        ) : section === 'redirects' ? (
          <RedirectsSection siteId={siteId} zone={zone} may={may} />
        ) : section === 'cache' ? (
          <CacheSection siteId={siteId} zone={zone} may={may} />
        ) : (
          <NameServersSection siteId={siteId} zone={zone} may={may} />
        )
      ) : null}
    </div>
  );
}
